/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import {
  BrickLoader,
  UniversalWrap,
  CostTableData,
  mapAccumulatedCostChartColors,
  StackBarChart,
  getCostAnalysisTableColumns,
  ChartType,
  Granularity,
  Period,
} from "~/components";
import { useCurrency } from "~/hooks";
import { useApplicationsAccumulatedCost } from "../hooks";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  showCostTable: boolean;
};

export function ApplicationsAccumulatedCostView({
  startDate,
  endDate,
  granularity,
  period,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();

  const {
    accumulatedCostKeys,
    accumulatedCostChartData,
    accumulatedCostTableData,
    accumulatedCostTotal,
    loading,
    error,
  } = useApplicationsAccumulatedCost({
    startDate,
    endDate,
    granularity,
  });

  const colors = mapAccumulatedCostChartColors(accumulatedCostKeys);

  const allColumns = useMemo(
    () =>
      getCostAnalysisTableColumns({
        startDate,
        endDate,
        currency,
        data: accumulatedCostTableData,
        period,
        colors,
        chartType: ChartType.ACCUMULATED_COST,
      }),
    [startDate, endDate, currency, accumulatedCostTableData, period, colors]
  );

  // hide total column when period is month
  const columns = useMemo(
    () =>
      period === Period.MONTH
        ? allColumns.filter((column) => column.header !== "Total")
        : allColumns,
    [allColumns, period]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <StackBarChart
            width={parent.width}
            height={280}
            data={accumulatedCostChartData}
            granularity={granularity}
            loading={loading}
            startDate={startDate}
            endDate={endDate}
            period={period}
            keys={accumulatedCostKeys}
            colors={colors}
            chartType={ChartType.ACCUMULATED_COST}
          />
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={accumulatedCostTableData}
        chartType={ChartType.ACCUMULATED_COST}
        total={accumulatedCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={false}
        hideHeader={period === Period.MONTH}
      />
    </UniversalWrap>
  );
}
